import React, { FC, useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import AudioPlayer from './AudioPlayer';
//@ts-ignore
import { debounce } from "debounce";

interface SubtitleWord {
  start: number;
  end: number;
  word: string;
}

interface SubtitleLine {
  words: SubtitleWord[];
}
interface Subtitle {
  lines: SubtitleLine[];
}

interface DictionaryEntry {
  'definition-es': string;
  pronunciation: string;
  'word-type': string[];
}

interface PlayingState {
   playing: boolean;
   blur: number;
   showOverlay: boolean;
}

type Dictionary = Map<string, DictionaryEntry> | null;

type SubtitleType = Subtitle | null;

const VideoPlayer: FC = () => {
  const [subtitles, setSubtitles] = useState<SubtitleType>(null);
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [inSubtitleRange, setInSubtitleRange] = useState(false);
  const [dictionary, setDictionary] = useState<Dictionary>(null);
  const [playingState, setPlayingState] = useState<PlayingState>({
     playing: true,
     blur: 0,
     showOverlay: false
  });

  const width: string = '45.63rem';
  const height: string = '25.66rem';

  const onProgressCallback = ({ playedSeconds }: { playedSeconds: number }) => {
    playedSeconds = playedSeconds * 1000; // transform into milliseconds
    updateCurrentSubtitleIfNeeded(playedSeconds);
    updateCurrentWordIfNeeded(playedSeconds);
  };

  const updateCurrentWordIfNeeded = (currentTime: number) => {
    if (inSubtitleRange) {
      updateCurrentWord(currentTime);
    }
  };

  const updateCurrentWord = (currentTime: number) => {
    const lineMetadata = subtitles?.lines[currentSubtitleIndex];
    const wordMetadata = lineMetadata?.words[currentWordIndex];

    if (
      lineMetadata &&
      wordMetadata &&
      currentTime > wordMetadata.end &&
      currentWordIndex + 1 < lineMetadata.words.length
    ) {
      setCurrentWordIndex(currentWordIndex + 1);
    }
  };

  const subtitleBegan = (currentTime: number): boolean => {
    const getSubtitleLineStart = (subtitleLine: SubtitleLine): number => {
      return subtitleLine.words[0].start;
    };

    const subtitleLine = subtitles?.lines[currentSubtitleIndex];
    const subtitleBeganValue = !!subtitleLine && getSubtitleLineStart(subtitleLine) <= currentTime;
    return subtitleBeganValue;
  };

  const updateCurrentSubtitleIfNeeded = (currentTime: number): void => {
    if (subtitleBegan(currentTime)) {
      updateCurrentSubtitle(currentTime);
    }
  };

  const updateCurrentSubtitle = (currentTime: number): void => {
    if (!inSubtitleRange) {
      setInSubtitleRange(true);
    } else if (subtitleNeedsUpdate(currentTime)) {
      setInSubtitleRange(false);
      setCurrentSubtitleIndex(currentSubtitleIndex + 1);
      setCurrentWordIndex(0);
    }
  };

  const subtitleNeedsUpdate = (currentTime: number): boolean => {
    const getSubtitleLineEnd = (subtitleLine: SubtitleLine): number => {
      return subtitleLine.words[subtitleLine.words.length - 1].end;
    };

    const subtitleLine = subtitles?.lines[currentSubtitleIndex];

    return !!subtitleLine && getSubtitleLineEnd(subtitleLine) < currentTime;
  };

  const getSubtitlesRaw = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      resolve(`
      {
        "lines":[
           {
              "words":[
                 {
                    "start":14000,
                    "end":14800,
                    "word":"I"
                 },
                 {
                    "start":14800,
                    "end":17069,
                    "word":"heard"
                 },
                 {
                    "start":16016,
                    "end":17630,
                    "word":"that"
                 },
                 {
                    "start":17842,
                    "end":18751,
                    "word":"you\u2032re"
                 },
                 {
                    "start":19351,
                    "end":19982,
                    "word":"settled"
                 },
                 {
                    "start":20046,
                    "end":20989,
                    "word":"down"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":20989,
                    "end":21355,
                    "word":"That"
                 },
                 {
                    "start":21534,
                    "end":22501,
                    "word":"you"
                 },
                 {
                    "start":22957,
                    "end":23339,
                    "word":"found"
                 },
                 {
                    "start":23370,
                    "end":23602,
                    "word":"a"
                 },
                 {
                    "start":23649,
                    "end":24654,
                    "word":"girl"
                 },
                 {
                    "start":24814,
                    "end":25031,
                    "word":"and"
                 },
                 {
                    "start":25062,
                    "end":25800,
                    "word":"you\u2032re"
                 },
                 {
                    "start":26496,
                    "end":26975,
                    "word":"married"
                 },
                 {
                    "start":27091,
                    "end":29467,
                    "word":"now"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":30007,
                    "end":30625,
                    "word":"I"
                 },
                 {
                    "start":30750,
                    "end":31620,
                    "word":"heard"
                 },
                 {
                    "start":31818,
                    "end":32087,
                    "word":"that"
                 },
                 {
                    "start":32173,
                    "end":32567,
                    "word":"your"
                 },
                 {
                    "start":32628,
                    "end":33614,
                    "word":"dreams"
                 },
                 {
                    "start":33667,
                    "end":34224,
                    "word":"came"
                 },
                 {
                    "start":34490,
                    "end":35087,
                    "word":"true"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":35652,
                    "end":35978,
                    "word":"Guess"
                 },
                 {
                    "start":36062,
                    "end":36320,
                    "word":"she"
                 },
                 {
                    "start":36330,
                    "end":36656,
                    "word":"gave"
                 },
                 {
                    "start":36747,
                    "end":36854,
                    "word":"you"
                 },
                 {
                    "start":36983,
                    "end":38045,
                    "word":"things"
                 },
                 {
                    "start":39039,
                    "end":39361,
                    "word":"I"
                 },
                 {
                    "start":39361,
                    "end":39708,
                    "word":"didn\u2032t"
                 },
                 {
                    "start":39875,
                    "end":40135,
                    "word":"give"
                 },
                 {
                    "start":40190,
                    "end":40339,
                    "word":"to"
                 },
                 {
                    "start":40339,
                    "end":42351,
                    "word":"you"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":43106,
                    "end":43995,
                    "word":"Old"
                 },
                 {
                    "start":44273,
                    "end":45092,
                    "word":"friend"
                 },
                 {
                    "start":45873,
                    "end":46454,
                    "word":"why"
                 },
                 {
                    "start":46454,
                    "end":46554,
                    "word":"are"
                 },
                 {
                    "start":46554,
                    "end":46654,
                    "word":"you"
                 },
                 {
                    "start":46857,
                    "end":47421,
                    "word":"so"
                 },
                 {
                    "start":47566,
                    "end":48933,
                    "word":"shy"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":49665,
                    "end":49777,
                    "word":"Ain\u2032t"
                 },
                 {
                    "start":50070,
                    "end":50522,
                    "word":"like"
                 },
                 {
                    "start":50522,
                    "end":50878,
                    "word":"you"
                 },
                 {
                    "start":50959,
                    "end":51163,
                    "word":"to"
                 },
                 {
                    "start":51299,
                    "end":51993,
                    "word":"hold"
                 },
                 {
                    "start":52076,
                    "end":52859,
                    "word":"back"
                 },
                 {
                    "start":53328,
                    "end":54021,
                    "word":"or"
                 },
                 {
                    "start":54173,
                    "end":54931,
                    "word":"hide"
                 },
                 {
                    "start":55213,
                    "end":55388,
                    "word":"from"
                 },
                 {
                    "start":55388,
                    "end":55569,
                    "word":"the"
                 },
                 {
                    "start":55758,
                    "end":56706,
                    "word":"light"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":57478,
                    "end":57625,
                    "word":"I"
                 },
                 {
                    "start":57635,
                    "end":57773,
                    "word":"hate"
                 },
                 {
                    "start":57922,
                    "end":58043,
                    "word":"to"
                 },
                 {
                    "start":58224,
                    "end":58409,
                    "word":"turn"
                 },
                 {
                    "start":58571,
                    "end":58772,
                    "word":"up"
                 },
                 {
                    "start":58918,
                    "end":59036,
                    "word":"out"
                 },
                 {
                    "start":59201,
                    "end":59384,
                    "word":"of"
                 },
                 {
                    "start":59395,
                    "end":59450,
                    "word":"the"
                 },
                 {
                    "start":59615,
                    "end":59930,
                    "word":"blue"
                 },
                 {
                    "start":60103,
                    "end":60662,
                    "word":"uninvited"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":60839,
                    "end":60967,
                    "word":"But"
                 },
                 {
                    "start":61092,
                    "end":61592,
                    "word":"I"
                 },
                 {
                    "start":61803,
                    "end":61912,
                    "word":"couldn\u2032t"
                 },
                 {
                    "start":62074,
                    "end":62369,
                    "word":"stay"
                 },
                 {
                    "start":62426,
                    "end":63206,
                    "word":"away"
                 },
                 {
                    "start":63364,
                    "end":63542,
                    "word":"I"
                 },
                 {
                    "start":63552,
                    "end":63788,
                    "word":"couldn\u2032t"
                 },
                 {
                    "start":63871,
                    "end":64123,
                    "word":"fight"
                 },
                 {
                    "start":64134,
                    "end":64220,
                    "word":"it"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":64004,
                    "end":64219,
                    "word":"I"
                 },
                 {
                    "start":64219,
                    "end":64433,
                    "word":"had"
                 },
                 {
                    "start":64433,
                    "end":64648,
                    "word":"hoped"
                 },
                 {
                    "start":64648,
                    "end":64862,
                    "word":"You"
                 },
                 {
                    "start":64862,
                    "end":65077,
                    "word":"had"
                 },
                 {
                    "start":65077,
                    "end":65291,
                    "word":"see"
                 },
                 {
                    "start":65291,
                    "end":65506,
                    "word":"my"
                 },
                 {
                    "start":65506,
                    "end":65720,
                    "word":"face"
                 },
                 {
                    "start":65720,
                    "end":65935,
                    "word":"and"
                 },
                 {
                    "start":65935,
                    "end":66149,
                    "word":"that"
                 },
                 {
                    "start":66149,
                    "end":66364,
                    "word":"You"
                 },
                 {
                    "start":66364,
                    "end":66578,
                    "word":"had"
                 },
                 {
                    "start":66578,
                    "end":66793,
                    "word":"be"
                 },
                 {
                    "start":66793,
                    "end":67007,
                    "word":"reminded"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":67855,
                    "end":68105,
                    "word":"That"
                 },
                 {
                    "start":68105,
                    "end":68277,
                    "word":"for"
                 },
                 {
                    "start":68380,
                    "end":69077,
                    "word":"me"
                 },
                 {
                    "start":69486,
                    "end":69631,
                    "word":"it"
                 },
                 {
                    "start":69631,
                    "end":69878,
                    "word":"isn\u2032t"
                 },
                 {
                    "start":70145,
                    "end":72075,
                    "word":"over"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":73976,
                    "end":74200,
                    "word":"Never"
                 },
                 {
                    "start":74200,
                    "end":74770,
                    "word":"mind"
                 },
                 {
                    "start":74770,
                    "end":75081,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":75081,
                    "end":76033,
                    "word":"find"
                 },
                 {
                   "start":76376,
                   "end":76991,
                   "word":"someone"
                 },
                 {
                    "start":76991,
                    "end":77471,
                    "word":"like"
                 },
                 {
                    "start":77471,
                    "end":79756,
                    "word":"you"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":80149,
                    "end":80384,
                    "word":"I"
                 },
                 {
                    "start":80384,
                    "end":80672,
                    "word":"wish"
                 },
                 {
                    "start":80672,
                    "end":81453,
                    "word":"nothing"
                 },
                 {
                    "start":81453,
                    "end":81930,
                    "word":"but"
                 },
                 {
                    "start":81930,
                    "end":82352,
                    "word":"the"
                 },
                 {
                    "start":82352,
                    "end":83126,
                    "word":"best"
                 },
                 {
                    "start":83790,
                    "end":84321,
                    "word":"for"
                 },
                 {
                    "start":84321,
                    "end":85657,
                    "word":"you"
                 },
                 {
                    "start":86816,
                    "end":87027,
                    "word":"too"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":87027,
                    "end":87628,
                    "word":"Don\u2032t"
                 },
                 {
                    "start":87628,
                    "end":88228,
                    "word":"forget"
                 },
                 {
                    "start":88228,
                    "end":88829,
                    "word":"me"
                 },
                 {
                    "start":88829,
                    "end":89429,
                    "word":"I"
                 },
                 {
                    "start":89429,
                    "end":90030,
                    "word":"beg"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":90585,
                    "end":90952,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":90952,
                    "end":92246,
                    "word":"remember"
                 },
                 {
                    "start":92654,
                    "end":92964,
                    "word":"you"
                 },
                 {
                    "start":92964,
                    "end":93913,
                    "word":"said"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":94120,
                    "end":94705,
                    "word":"Sometimes"
                 },
                 {
                    "start":94705,
                    "end":95045,
                    "word":"it"
                 },
                 {
                    "start":95045,
                    "end":95718,
                    "word":"lasts"
                 },
                 {
                    "start":95718,
                    "end":96024,
                    "word":"in"
                 },
                 {
                    "start":96024,
                    "end":96449,
                    "word":"love"
                 },
                 {
                    "start":96449,
                    "end":96788,
                    "word":"but"
                 },
                 {
                    "start":96788,
                    "end":97453,
                    "word":"sometimes"
                 },
                 {
                    "start":97453,
                    "end":97797,
                    "word":"it"
                 },
                 {
                    "start":97797,
                    "end":98326,
                    "word":"hurts"
                 },
                 {
                    "start":98326,
                    "end":100826,
                    "word":"instead"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":101000,
                    "end":101585,
                    "word":"Sometimes"
                 },
                 {
                    "start":101585,
                    "end":101925,
                    "word":"it"
                 },
                 {
                    "start":101925,
                    "end":102598,
                    "word":"lasts"
                 },
                 {
                    "start":102598,
                    "end":102904,
                    "word":"in"
                 },
                 {
                    "start":102904,
                    "end":103329,
                    "word":"love"
                 },
                 {
                    "start":103329,
                    "end":103668,
                    "word":"but"
                 },
                 {
                    "start":103668,
                    "end":104333,
                    "word":"sometimes"
                 },
                 {
                    "start":104333,
                    "end":104677,
                    "word":"it"
                 },
                 {
                    "start":104677,
                    "end":105206,
                    "word":"hurts"
                 },
                 {
                    "start":105206,
                    "end":107706,
                    "word":"instead"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":111051,
                    "end":112052,
                    "word":"You"
                 },
                 {
                    "start":112052,
                    "end":113053,
                    "word":"know"
                 },
                 {
                    "start":113053,
                    "end":114054,
                    "word":"how"
                 },
                 {
                    "start":114054,
                    "end":115055,
                    "word":"the"
                 },
                 {
                    "start":115055,
                    "end":116056,
                    "word":"time"
                 },
                 {
                    "start":116056,
                    "end":117057,
                    "word":"flies"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":117431,
                    "end":118234,
                    "word":"Only"
                 },
                 {
                    "start":119107,
                    "end":120734,
                    "word":"yesterday"
                 },
                 {
                    "start":120734,
                    "end":121144,
                    "word":"was"
                 },
                 {
                    "start":121144,
                    "end":121718,
                    "word":"the"
                 },
                 {
                    "start":121718,
                    "end":122461,
                    "word":"time"
                 },
                 {
                    "start":122725,
                    "end":122962,
                    "word":"of"
                 },
                 {
                    "start":122962,
                    "end":123217,
                    "word":"our"
                 },
                 {
                    "start":123217,
                    "end":124103,
                    "word":"lives"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":124204,
                    "end":124605,
                    "word":"We"
                 },
                 {
                    "start":124605,
                    "end":125205,
                    "word":"were"
                 },
                 {
                    "start":125205,
                    "end":125806,
                    "word":"born"
                 },
                 {
                    "start":125806,
                    "end":126406,
                    "word":"and"
                 },
                 {
                    "start":126406,
                    "end":127007,
                    "word":"raised"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":128008,
                    "end":128759,
                    "word":"In"
                 },
                 {
                    "start":128759,
                    "end":129510,
                    "word":"a"
                 },
                 {
                    "start":129510,
                    "end":130260,
                    "word":"summer"
                 },
                 {
                    "start":130260,
                    "end":131011,
                    "word":"haze"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":131440,
                    "end":132200,
                    "word":"Bound"
                 },
                 {
                    "start":132200,
                    "end":133005,
                    "word":"by"
                 },
                 {
                    "start":133005,
                    "end":133444,
                    "word":"the"
                 },
                 {
                    "start":133444,
                    "end":134499,
                    "word":"surprise"
                 },
                 {
                    "start":134680,
                    "end":135269,
                    "word":"of"
                 },
                 {
                    "start":135269,
                    "end":135813,
                    "word":"our"
                 },
                 {
                    "start":135946,
                    "end":137119,
                    "word":"glory"
                 },
                 {
                    "start":137747,
                    "end":139149,
                    "word":"days"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":139019,
                    "end":139319,
                    "word":"I"
                 },
                 {
                    "start":139319,
                    "end":139620,
                    "word":"hate"
                 },
                 {
                    "start":139620,
                    "end":139920,
                    "word":"to"
                 },
                 {
                    "start":139920,
                    "end":140220,
                    "word":"turn"
                 },
                 {
                    "start":140220,
                    "end":140521,
                    "word":"up"
                 },
                 {
                    "start":140521,
                    "end":140821,
                    "word":"out"
                 },
                 {
                    "start":140821,
                    "end":141121,
                    "word":"of"
                 },
                 {
                    "start":141121,
                    "end":141421,
                    "word":"the"
                 },
                 {
                    "start":141421,
                    "end":141722,
                    "word":"blue"
                 },
                 {
                    "start":141722,
                    "end":142022,
                    "word":"uninvited"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":142622,
                    "end":142873,
                    "word":"But"
                 },
                 {
                    "start":142873,
                    "end":143282,
                    "word":"I"
                 },
                 {
                    "start":143472,
                    "end":143709,
                    "word":"couldn\u2032t"
                 },
                 {
                    "start":143719,
                    "end":144159,
                    "word":"stay"
                 },
                 {
                    "start":144159,
                    "end":144790,
                    "word":"away"
                 },
                 {
                    "start":144987,
                    "end":145285,
                    "word":"I"
                 },
                 {
                    "start":145285,
                    "end":145444,
                    "word":"couldn\u2032t"
                 },
                 {
                    "start":145444,
                    "end":145840,
                    "word":"fight"
                 },
                 {
                    "start":145840,
                    "end":146030,
                    "word":"it"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":146026,
                    "end":146241,
                    "word":"I"
                 },
                 {
                    "start":146241,
                    "end":146455,
                    "word":"had"
                 },
                 {
                    "start":146455,
                    "end":146670,
                    "word":"hoped"
                 },
                 {
                    "start":146670,
                    "end":146884,
                    "word":"You"
                 },
                 {
                    "start":146884,
                    "end":147099,
                    "word":"had"
                 },
                 {
                    "start":147099,
                    "end":147313,
                    "word":"see"
                 },
                 {
                    "start":147313,
                    "end":147528,
                    "word":"my"
                 },
                 {
                    "start":147528,
                    "end":147742,
                    "word":"face"
                 },
                 {
                    "start":147742,
                    "end":147957,
                    "word":"and"
                 },
                 {
                    "start":147957,
                    "end":148171,
                    "word":"that"
                 },
                 {
                    "start":148171,
                    "end":148386,
                    "word":"You"
                 },
                 {
                    "start":148386,
                    "end":148600,
                    "word":"had"
                 },
                 {
                    "start":148600,
                    "end":148815,
                    "word":"be"
                 },
                 {
                    "start":148815,
                    "end":149029,
                    "word":"reminded"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":149769,
                    "end":149924,
                    "word":"That"
                 },
                 {
                    "start":149924,
                    "end":150099,
                    "word":"for"
                 },
                 {
                    "start":150099,
                    "end":150899,
                    "word":"me"
                 },
                 {
                    "start":151307,
                    "end":151535,
                    "word":"it"
                 },
                 {
                    "start":151535,
                    "end":151858,
                    "word":"isn\u2032t"
                 },
                 {
                    "start":151858,
                    "end":154894,
                    "word":"over"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":157503,
                    "end":157822,
                    "word":"Never"
                 },
                 {
                    "start":157822,
                    "end":158304,
                    "word":"mind"
                 },
                 {
                    "start":158304,
                    "end":158748,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":158748,
                    "end":159436,
                    "word":"find"
                 },
                 {
                    "start":159847,
                    "end":160463,
                    "word":"someone"
                 },
                 {
                    "start":160663,
                    "end":161075,
                    "word":"like"
                 },
                 {
                    "start":161075,
                    "end":162822,
                    "word":"you"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":163441,
                    "end":163804,
                    "word":"I"
                 },
                 {
                    "start":163963,
                    "end":164171,
                    "word":"wish"
                 },
                 {
                    "start":164171,
                    "end":165016,
                    "word":"nothing"
                 },
                 {
                    "start":165215,
                    "end":165455,
                    "word":"but"
                 },
                 {
                    "start":165455,
                    "end":165937,
                    "word":"the"
                 },
                 {
                    "start":165937,
                    "end":166909,
                    "word":"best"
                 },
                 {
                    "start":167270,
                    "end":167839,
                    "word":"for"
                 },
                 {
                    "start":167839,
                    "end":169504,
                    "word":"you"
                 },
                 {
                    "start":169743,
                    "end":170481,
                    "word":"too"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":170050,
                    "end":170651,
                    "word":"Don\u2032t"
                 },
                 {
                    "start":170651,
                    "end":171251,
                    "word":"forget"
                 },
                 {
                    "start":171251,
                    "end":171852,
                    "word":"me"
                 },
                 {
                    "start":171852,
                    "end":172452,
                    "word":"I"
                 },
                 {
                    "start":172452,
                    "end":173053,
                    "word":"beg"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":173945,
                    "end":174515,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":174515,
                    "end":175913,
                    "word":"remember"
                 },
                 {
                    "start":176242,
                    "end":176619,
                    "word":"you"
                 },
                 {
                    "start":176619,
                    "end":177189,
                    "word":"said"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":177628,
                    "end":178297,
                    "word":"Sometimes"
                 },
                 {
                    "start":178297,
                    "end":178507,
                    "word":"it"
                 },
                 {
                    "start":178507,
                    "end":179125,
                    "word":"lasts"
                 },
                 {
                    "start":179271,
                    "end":179416,
                    "word":"in"
                 },
                 {
                    "start":179416,
                    "end":179955,
                    "word":"love"
                 },
                 {
                    "start":180132,
                    "end":180310,
                    "word":"but"
                 },
                 {
                    "start":180310,
                    "end":180892,
                    "word":"sometimes"
                 },
                 {
                    "start":181078,
                    "end":181265,
                    "word":"it"
                 },
                 {
                    "start":181265,
                    "end":181736,
                    "word":"hurts"
                 },
                 {
                    "start":181866,
                    "end":184843,
                    "word":"instead"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":185625,
                    "end":186119,
                    "word":"Nothing"
                 },
                 {
                    "start":186296,
                    "end":187000,
                    "word":"compares"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":187180,
                    "end":187473,
                    "word":"No"
                 },
                 {
                    "start":187525,
                    "end":187933,
                    "word":"worries"
                 },
                 {
                    "start":188084,
                    "end":188270,
                    "word":"or"
                 },
                 {
                    "start":188483,
                    "end":188880,
                    "word":"cares"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":189114,
                    "end":189546,
                    "word":"Regrets"
                 },
                 {
                    "start":189664,
                    "end":189876,
                    "word":"and"
                 },
                 {
                    "start":189876,
                    "end":190545,
                    "word":"mistakes"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":190593,
                    "end":190799,
                    "word":"They"
                 },
                 {
                    "start":190799,
                    "end":190896,
                    "word":"are"
                 },
                 {
                    "start":190896,
                    "end":191783,
                    "word":"memories"
                 },
                 {
                    "start":191929,
                    "end":192454,
                    "word":"made"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":192762,
                    "end":193087,
                    "word":"Who"
                 },
                 {
                    "start":193232,
                    "end":193415,
                    "word":"would"
                 },
                 {
                    "start":193415,
                    "end":193738,
                    "word":"have"
                 },
                 {
                    "start":193738,
                    "end":194004,
                    "word":"known"
                 },
                 {
                    "start":194254,
                    "end":194704,
                    "word":"how"
                 },
                 {
                    "start":195471,
                    "end":197143,
                    "word":"bittersweet"
                 },
                 {
                    "start":198239,
                    "end":198776,
                    "word":"this"
                 },
                 {
                    "start":199140,
                    "end":199818,
                    "word":"would"
                 },
                 {
                    "start":200064,
                    "end":201805,
                    "word":"taste"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":204482,
                    "end":204812,
                    "word":"Never"
                 },
                 {
                    "start":204812,
                    "end":205205,
                    "word":"mind"
                 },
                 {
                    "start":205205,
                    "end":205648,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":205648,
                    "end":206489,
                    "word":"find"
                 },
                 {
                    "start":206881,
                    "end":207275,
                    "word":"someone"
                 },
                 {
                    "start":207421,
                    "end":207632,
                    "word":"like"
                 },
                 {
                    "start":207876,
                    "end":210165,
                    "word":"you"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":210436,
                    "end":210613,
                    "word":"I"
                 },
                 {
                    "start":210759,
                    "end":210951,
                    "word":"wish"
                 },
                 {
                    "start":211186,
                    "end":211724,
                    "word":"nothing"
                 },
                 {
                    "start":212015,
                    "end":212229,
                    "word":"but"
                 },
                 {
                    "start":212229,
                    "end":212676,
                    "word":"the"
                 },
                 {
                    "start":212887,
                    "end":213607,
                    "word":"best"
                 },
                 {
                    "start":214146,
                    "end":214603,
                    "word":"for"
                 },
                 {
                    "start":214849,
                    "end":217092,
                    "word":"you"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":217037,
                    "end":217638,
                    "word":"Don\u2032t"
                 },
                 {
                    "start":217638,
                    "end":218238,
                    "word":"forget"
                 },
                 {
                    "start":218238,
                    "end":218839,
                    "word":"me"
                 },
                 {
                    "start":218839,
                    "end":219439,
                    "word":"I"
                 },
                 {
                    "start":219439,
                    "end":220040,
                    "word":"beg"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":220040,
                    "end":221642,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":221642,
                    "end":222442,
                    "word":"remember"
                 },
                 {
                    "start":222442,
                    "end":223243,
                    "word":"you"
                 },
                 {
                    "start":223243,
                    "end":224044,
                    "word":"said"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":224571,
                    "end":225094,
                    "word":"Sometimes"
                 },
                 {
                    "start":225158,
                    "end":225341,
                    "word":"it"
                 },
                 {
                    "start":225341,
                    "end":225923,
                    "word":"lasts"
                 },
                 {
                    "start":226175,
                    "end":226391,
                    "word":"in"
                 },
                 {
                    "start":226391,
                    "end":226866,
                    "word":"love"
                 },
                 {
                    "start":227024,
                    "end":227174,
                    "word":"but"
                 },
                 {
                    "start":227174,
                    "end":227729,
                    "word":"sometimes"
                 },
                 {
                    "start":227729,
                    "end":227987,
                    "word":"it"
                 },
                 {
                    "start":228236,
                    "end":228694,
                    "word":"hurts"
                 },
                 {
                    "start":228694,
                    "end":231736,
                    "word":"instead"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":232677,
                    "end":233173,
                    "word":"Never"
                 },
                 {
                    "start":233173,
                    "end":233580,
                    "word":"mind"
                 },
                 {
                    "start":233749,
                    "end":233922,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":234069,
                    "end":234651,
                    "word":"find"
                 },
                 {
                    "start":235278,
                    "end":235729,
                    "word":"someone"
                 },
                 {
                    "start":236013,
                    "end":236313,
                    "word":"like"
                 },
                 {
                    "start":236416,
                    "end":237856,
                    "word":"you"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":238941,
                    "end":239151,
                    "word":"I"
                 },
                 {
                    "start":239151,
                    "end":239494,
                    "word":"wish"
                 },
                 {
                    "start":239695,
                    "end":240315,
                    "word":"nothing"
                 },
                 {
                    "start":240384,
                    "end":240760,
                    "word":"but"
                 },
                 {
                    "start":240811,
                    "end":241082,
                    "word":"the"
                 },
                 {
                    "start":241249,
                    "end":242193,
                    "word":"best"
                 },
                 {
                    "start":242611,
                    "end":243117,
                    "word":"for"
                 },
                 {
                    "start":243486,
                    "end":244809,
                    "word":"you"
                 },
                 {
                    "start":244262,
                    "end":245796,
                    "word":"too"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":245965,
                    "end":246311,
                    "word":"Don\u2032t"
                 },
                 {
                    "start":246321,
                    "end":246934,
                    "word":"forget"
                 },
                 {
                    "start":246949,
                    "end":247564,
                    "word":"me"
                 },
                 {
                    "start":247855,
                    "end":248117,
                    "word":"I"
                 },
                 {
                    "start":248324,
                    "end":249116,
                    "word":"beg"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":249576,
                    "end":249828,
                    "word":"I\u2032ll"
                 },
                 {
                    "start":249906,
                    "end":251427,
                    "word":"remember"
                 },
                 {
                    "start":251454,
                    "end":251779,
                    "word":"you"
                 },
                 {
                    "start":251926,
                    "end":252793,
                    "word":"said"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":252902,
                    "end":253554,
                    "word":"Sometimes"
                 },
                 {
                    "start":253714,
                    "end":253830,
                    "word":"it"
                 },
                 {
                    "start":253900,
                    "end":254453,
                    "word":"lasts"
                 },
                 {
                    "start":254649,
                    "end":254815,
                    "word":"in"
                 },
                 {
                    "start":254825,
                    "end":255295,
                    "word":"love"
                 },
                 {
                    "start":255331,
                    "end":255548,
                    "word":"but"
                 },
                 {
                    "start":255548,
                    "end":256305,
                    "word":"sometimes"
                 },
                 {
                    "start":256353,
                    "end":256499,
                    "word":"it"
                 },
                 {
                    "start":256571,
                    "end":256937,
                    "word":"hurts"
                 },
                 {
                    "start":257312,
                    "end":259884,
                    "word":"instead"
                 }
              ]
           },
           {
              "words":[
                 {
                    "start":260095,
                    "end":260855,
                    "word":"Sometimes"
                 },
                 {
                    "start":260893,
                    "end":261011,
                    "word":"it"
                 },
                 {
                    "start":261021,
                    "end":261601,
                    "word":"lasts"
                 },
                 {
                    "start":261748,
                    "end":261880,
                    "word":"in"
                 },
                 {
                    "start":261940,
                    "end":262480,
                    "word":"love"
                 },
                 {
                    "start":262531,
                    "end":262681,
                    "word":"but"
                 },
                 {
                    "start":262691,
                    "end":263773,
                    "word":"sometimes"
                 },
                 {
                    "start":263783,
                    "end":263800,
                    "word":"it"
                 },
                 {
                    "start":264000,
                    "end":264371,
                    "word":"hurts"
                 },
                 {
                    "start":264491,
                    "end":268577,
                    "word":"instead"
                 }
              ]
           }
        ]
     }
      `);
    });
  };

  const getDictionaryRaw = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      resolve(`
      {
        "a":{
           "definition-es":"un",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/5/52/En-us-a.ogg",
           "word-type":[
              "letter",
              "numeral",
              "noun"
           ]
        },
        "ain\u2032t":{
           "definition-es":"no es",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "and":{
           "definition-es":"y",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/2/26/En-us-and.ogg",
           "word-type":[
              "conjunction",
              "noun"
           ]
        },
        "are":{
           "definition-es":"est\u00e1n",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/69/En-us-are.ogg",
           "word-type":[
              "verb"
           ]
        },
        "away\u201a":{
           "definition-es":"fuera,",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "b":{
           "definition-es":"B",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/93/En-us-b.ogg",
           "word-type":[
              "letter"
           ]
        },
        "back":{
           "definition-es":"espalda",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/e/ec/En-us-back.ogg",
           "word-type":[
              "adjective",
              "adverb",
              "noun",
              "verb"
           ]
        },
        "be":{
           "definition-es":"ser",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/fa/En-uk-to_be.ogg",
           "word-type":[
              "verb"
           ]
        },
        "beg":{
           "definition-es":"mendigar",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/8/82/En-us-beg.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "best":{
           "definition-es":"mejor",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/3/3a/En-us-best.ogg",
           "word-type":[
              "adjective",
              "adverb",
              "noun",
              "verb"
           ]
        },
        "bitt":{
           "definition-es":"morder",
           "pronunciation":"",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "bl":{
           "definition-es":"compartimiento",
           "pronunciation":"",
           "word-type":[
              "adjective"
           ]
        },
        "born":{
           "definition-es":"Naci\u00f3",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/6f/En-us-born.ogg",
           "word-type":[
              "verb",
              "adjective"
           ]
        },
        "bound":{
           "definition-es":"atado",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/7f/En-us-bound.ogg",
           "word-type":[
              "verb",
              "adjective"
           ]
        },
        "bu":{
           "definition-es":"eso",
           "pronunciation":"",
           "word-type":[
              "noun"
           ]
        },
        "but":{
           "definition-es":"pero",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/7d/En-us-but.ogg",
           "word-type":[
              "preposition",
              "adverb",
              "conjunction",
              "noun",
              "verb"
           ]
        },
        "by":{
           "definition-es":"por",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/c/c8/En-us-by.ogg",
           "word-type":[
              "preposition",
              "adverb",
              "adjective",
              "noun",
              "interjection"
           ]
        },
        "c":{
           "definition-es":"C",
           "pronunciation":"",
           "word-type":[
              "letter"
           ]
        },
        "came":{
           "definition-es":"lleg\u00f3",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/7d/En-us-came.ogg",
           "word-type":[
              "verb",
              "preposition"
           ]
        },
        "cares":{
           "definition-es":"cuidar",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/78/En-us-cares.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "compares":{
           "definition-es":"compara",
           "pronunciation":"",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "couldn\u2032t":{
           "definition-es":"no pod\u00eda",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "d":{
           "definition-es":"D",
           "pronunciation":"",
           "word-type":[
              "letter"
           ]
        },
        "days":{
           "definition-es":"dias",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/3/3c/En-us-days.ogg",
           "word-type":[
              "noun",
              "noun",
              "verb"
           ]
        },
        "don\u2032t":{
           "definition-es":"no",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "down":{
           "definition-es":"abajo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/8/89/En-us-down.ogg",
           "word-type":[
              "adverb",
              "preposition",
              "adjective",
              "verb",
              "noun"
           ]
        },
        "dreams":{
           "definition-es":"Sue\u00f1os",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/bf/En-us-dreams.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "ersweet":{
           "definition-es":"ersweet",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "face":{
           "definition-es":"cara",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/73/En-us-face.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "fight":{
           "definition-es":"lucha",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/6f/En-uk-fight.ogg",
           "word-type":[
              "verb"
           ]
        },
        "find":{
           "definition-es":"encontrar",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/48/En-us-find.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "flies":{
           "definition-es":"moscas",
           "pronunciation":"",
           "word-type":[
              "noun",
              "noun",
              "verb"
           ]
        },
        "for":{
           "definition-es":"por",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/67/En-us-for.ogg",
           "word-type":[
              "conjunction",
              "preposition"
           ]
        },
        "forget":{
           "definition-es":"olvidar",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/2/2b/En-uk-to_forget.ogg",
           "word-type":[
              "verb"
           ]
        },
        "found":{
           "definition-es":"encontr\u00f3",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/1/13/En-us-found.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "friend":{
           "definition-es":"amigo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/8/89/En-us-friend.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "from":{
           "definition-es":"de",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/70/En-us-from.ogg",
           "word-type":[
              "preposition"
           ]
        },
        "gave":{
           "definition-es":"dio",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/bb/En-us-gave.ogg",
           "word-type":[
              "verb"
           ]
        },
        "girl":{
           "definition-es":"ni\u00f1a",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/f3/En-us-girl.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "give":{
           "definition-es":"dar",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/69/En-us-give.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "glory":{
           "definition-es":"gloria",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/5/59/En-us-glory.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "guess":{
           "definition-es":"adivinar",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/f1/En-us-guess.ogg",
           "word-type":[
              "verb"
           ]
        },
        "had":{
           "definition-es":"tuvo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/68/En-us-had.ogg",
           "word-type":[
              "verb",
              "adjective"
           ]
        },
        "hate":{
           "definition-es":"odio",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/transcoded/f/f1/LL-Q1860_%28eng%29-Back_ache-hate.wav/LL-Q1860_%28eng%29-Back_ache-hate.wav.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "have":{
           "definition-es":"tengo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/be/En-us-have.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "haze":{
           "definition-es":"calina",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/transcoded/0/0d/LL-Q1860_%28eng%29-Vealhurl-haze.wav/LL-Q1860_%28eng%29-Vealhurl-haze.wav.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "heard":{
           "definition-es":"Escuch\u00f3",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/ae/En-us-heard.ogg",
           "word-type":[
              "verb",
              "adjective"
           ]
        },
        "hide":{
           "definition-es":"esconder",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/e/ee/En-us-hide.ogg",
           "word-type":[
              "verb"
           ]
        },
        "hold":{
           "definition-es":"mantener",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/fe/En-us-hold.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "hoped":{
           "definition-es":"esperanzado",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/c/c5/En-us-hoped.ogg",
           "word-type":[
              "verb"
           ]
        },
        "how":{
           "definition-es":"c\u00f3mo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/c/cc/En-uk-how.ogg",
           "word-type":[
              "adverb",
              "noun",
              "conjunction"
           ]
        },
        "hurts":{
           "definition-es":"duele",
           "pronunciation":"",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "i":{
           "definition-es":"I",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/d/de/En-us-i.ogg",
           "word-type":[
              "letter",
              "noun"
           ]
        },
        "idn\u2032t":{
           "definition-es":"Yo no",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "in":{
           "definition-es":"en",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/fb/En-us-in.ogg",
           "word-type":[
              "preposition",
              "verb",
              "adverb",
              "noun",
              "adjective"
           ]
        },
        "instead":{
           "definition-es":"en lugar de",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/a5/En-us-instead.ogg",
           "word-type":[
              "adverb"
           ]
        },
        "isn\u2032t":{
           "definition-es":"no es",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "it":{
           "definition-es":"eso",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/43/En-uk-it.ogg",
           "word-type":[
              "pronoun",
              "determiner",
              "noun",
              "adjective"
           ]
        },
        "know":{
           "definition-es":"saber",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/ad/En-us-know.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "known":{
           "definition-es":"conocido",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/e/e3/En-us-known.ogg",
           "word-type":[
              "adjective",
              "noun",
              "verb"
           ]
        },
        "lasts":{
           "definition-es":"tiene una duraci\u00f3n",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/46/En-us-lasts.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "light":{
           "definition-es":"luz",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/ac/En-uk-light.ogg",
           "word-type":[
              "noun"
           ]
        },
        "like":{
           "definition-es":"como",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/99/En-uk-to_like.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "lives":{
           "definition-es":"vive",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/a1/En-us-lives-verb.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "love":{
           "definition-es":"amor",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/9c/En-uk-love.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "made":{
           "definition-es":"hecho",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/5/57/En-us-made.ogg",
           "word-type":[
              "noun"
           ]
        },
        "married":{
           "definition-es":"casado",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/9d/En-us-married.ogg",
           "word-type":[
              "adjective",
              "verb",
              "noun"
           ]
        },
        "me":{
           "definition-es":"me",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/c/ca/En-us-me.ogg",
           "word-type":[
              "pronoun"
           ]
        },
        "memories":{
           "definition-es":"recuerdos",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/b9/En-us-memories.ogg",
           "word-type":[
              "noun"
           ]
        },
        "me\u201a":{
           "definition-es":"me,",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "mind\u201a":{
           "definition-es":"mente,",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "mistakes":{
           "definition-es":"errores",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/5/5f/En-us-mistakes.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "my":{
           "definition-es":"mi",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/43/En-us-my.ogg",
           "word-type":[
              "determiner"
           ]
        },
        "nd":{
           "definition-es":"Dakota del Norte",
           "pronunciation":"",
           "word-type":[
              "conjunction"
           ]
        },
        "never":{
           "definition-es":"Nunca",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/ae/En-uk-never.ogg",
           "word-type":[
              "adverb"
           ]
        },
        "no":{
           "definition-es":"No",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/91/En-uk-no.ogg",
           "word-type":[
              "determiner"
           ]
        },
        "nothing":{
           "definition-es":"nada",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/b5/En-us-nothing.ogg",
           "word-type":[
              "pronoun",
              "noun",
              "adverb"
           ]
        },
        "now":{
           "definition-es":"ahora",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/9e/En-us-now.ogg",
           "word-type":[
              "adjective",
              "adverb",
              "conjunction",
              "interjection",
              "noun"
           ]
        },
        "o":{
           "definition-es":"O",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/99/En-uk-o.ogg",
           "word-type":[
              "letter",
              "noun"
           ]
        },
        "of":{
           "definition-es":"de",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/75/En-us-of.ogg",
           "word-type":[
              "preposition"
           ]
        },
        "old":{
           "definition-es":"viejo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/4f/En-uk-old.ogg",
           "word-type":[
              "adjective",
              "noun"
           ]
        },
        "only":{
           "definition-es":"solo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/c/ca/En-us-only.ogg",
           "word-type":[
              "adjective",
              "adverb",
              "conjunction",
              "noun"
           ]
        },
        "or":{
           "definition-es":"o",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/5/5a/En-uk-or.ogg",
           "word-type":[
              "conjunction"
           ]
        },
        "ouldn\u2032t":{
           "definition-es":"no deber\u00eda",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "our":{
           "definition-es":"nuestro",
           "pronunciation":"",
           "word-type":[
              "determiner",
              "verb"
           ]
        },
        "out":{
           "definition-es":"fuera",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/f1/En-us-out.ogg",
           "word-type":[
              "adverb",
              "preposition",
              "noun",
              "verb",
              "adjective",
              "interjection"
           ]
        },
        "over":{
           "definition-es":"encima",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/2/20/En-us-over.ogg",
           "word-type":[
              "adjective",
              "adverb",
              "noun",
              "preposition",
              "interjection",
              "verb"
           ]
        },
        "r":{
           "definition-es":"r",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/2/29/En-uk-r.ogg",
           "word-type":[
              "letter"
           ]
        },
        "raised":{
           "definition-es":"criado",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/49/En-us-raised.ogg",
           "word-type":[
              "verb"
           ]
        },
        "regrets":{
           "definition-es":"arrepentimientos",
           "pronunciation":"",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "remember":{
           "definition-es":"recuerda",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/0/05/En-us-remember.ogg",
           "word-type":[
              "verb"
           ]
        },
        "reminded":{
           "definition-es":"recordado",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/a9/En-us-reminded.ogg",
           "word-type":[
              "verb"
           ]
        },
        "rentanadvisercom":{
           "definition-es":"Rentanadvisercom.",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "said":{
           "definition-es":"dijo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/d/dc/En-us-said.ogg",
           "word-type":[
              "verb",
              "adjective",
              "determiner"
           ]
        },
        "see":{
           "A-es":"ver",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/6d/En-uk-to_see.ogg",
           "word-type":[
              "verb",
              "interjection"
           ]
        },
        "settled":{
           "definition-es":"asentados",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/3/3a/En-us-settled.ogg",
           "word-type":[
              "adjective",
              "verb"
           ]
        },
        "she":{
           "definition-es":"ella",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/b3/En-us-she.ogg",
           "word-type":[
              "pronoun",
              "determiner",
              "noun"
           ]
        },
        "shy":{
           "definition-es":"timido",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/8/8a/En-us-shy.ogg",
           "word-type":[
              "adjective",
              "verb",
              "noun"
           ]
        },
        "so":{
           "definition-es":"entonces",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/3/3b/En-us-so.ogg",
           "word-type":[
              "conjunction",
              "adverb",
              "adjective",
              "interjection"
           ]
        },
        "someone":{
           "definition-es":"alguien",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/2/2a/En-us-someone.ogg",
           "word-type":[
              "pronoun",
              "noun"
           ]
        },
        "sometimes":{
           "definition-es":"algunas veces",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/c/c0/En-us-sometimes.ogg",
           "word-type":[
              "adverb",
              "adjective"
           ]
        },
        "stay":{
           "definition-es":"permanecer",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/0/0a/En-us-stay.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "summer":{
           "definition-es":"verano",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/8/8a/En-us-summer.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "surprise":{
           "definition-es":"sorpresa",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/f2/En-us-surprise.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "t":{
           "definition-es":"t",
           "pronunciation":"",
           "word-type":[
              "letter"
           ]
        },
        "taste":{
           "definition-es":"sabor",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/a/a2/En-us-taste.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "that":{
           "definition-es":"que",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/e/e8/En-us-that.ogg",
           "word-type":[
              "conjunction",
              "determiner",
              "pronoun",
              "adverb",
              "noun"
           ]
        },
        "the":{
           "definition-es":"la",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/d/db/En-us-the-stressed.ogg",
           "word-type":[
              "article"
           ]
        },
        "they":{
           "definition-es":"ellos",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/41/En-us-they.ogg",
           "word-type":[
              "pronoun",
              "determiner"
           ]
        },
        "things":{
           "definition-es":"cosas",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/0/05/En-us-things.ogg",
           "word-type":[
              "noun",
              "noun",
              "verb"
           ]
        },
        "this":{
           "definition-es":"esto",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/f/f8/En-us-this.ogg",
           "word-type":[
              "determiner",
              "adverb",
              "pronoun",
              "noun",
              "interjection"
           ]
        },
        "time":{
           "definition-es":"hora",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/1/1b/En-us-time.ogg",
           "word-type":[
              "noun",
              "verb",
              "interjection"
           ]
        },
        "to":{
           "definition-es":"a",
           "pronunciation":"",
           "word-type":[
              "preposition",
              "adverb"
           ]
        },
        "too":{
           "definition-es":"tambi\u00e9n",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/b/bf/En-us-too.ogg",
           "word-type":[
              "adverb"
           ]
        },
        "true":{
           "definition-es":"cierto",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/e/e7/En-us-true.ogg",
           "word-type":[
              "adjective",
              "adverb",
              "noun",
              "verb"
           ]
        },
        "turn":{
           "definition-es":"turno",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/79/En-us-turn.ogg",
           "word-type":[
              "verb"
           ]
        },
        "uninvited":{
           "definition-es":"no invitado",
           "pronunciation":"",
           "word-type":[
              "adjective"
           ]
        },
        "up":{
           "definition-es":"arriba",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/9e/En-us-up.ogg",
           "word-type":[
              "adverb",
              "preposition",
              "adjective",
              "noun",
              "verb"
           ]
        },
        "ut":{
           "definition-es":"fuera",
           "pronunciation":"",
           "word-type":[
              "noun"
           ]
        },
        "was":{
           "definition-es":"estaba",
           "pronunciation":"",
           "word-type":[
              "verb"
           ]
        },
        "we":{
           "definition-es":"nosotros",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/9f/En-us-we.ogg",
           "word-type":[
              "pronoun",
              "determiner"
           ]
        },
        "were":{
           "definition-es":"fueron",
           "pronunciation":"",
           "word-type":[
              "verb"
           ]
        },
        "who":{
           "definition-es":"OMS",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/1/10/En-us-who.ogg",
           "word-type":[
              "pronoun",
              "noun",
              "determiner"
           ]
        },
        "why":{
           "definition-es":"por qu\u00e9",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/9/96/En-ne-why.ogg",
           "word-type":[
              "adverb",
              "noun",
              "interjection"
           ]
        },
        "will":{
           "definition-es":"voluntad",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/6/67/En-us-will.ogg",
           "word-type":[
              "verb"
           ]
        },
        "wish":{
           "definition-es":"deseo",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/7/7e/En-us-wish.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "worries":{
           "definition-es":"preocupaciones",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/e/e0/En-us-worries.ogg",
           "word-type":[
              "noun",
              "verb"
           ]
        },
        "would":{
           "definition-es":"har\u00eda",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/1/18/En-us-would.ogg",
           "word-type":[
              "verb",
              "noun"
           ]
        },
        "wwwrentanadvisercom":{
           "definition-es":"wwwrentanadvisercom",
           "pronunciation":"",
           "word-type":[
              
           ]
        },
        "yesterday":{
           "definition-es":"ayer",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/4/43/En-us-yesterday.ogg",
           "word-type":[
              "noun",
              "adverb"
           ]
        },
        "you":{
           "definition-es":"usted",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/1/13/En-uk-you.ogg",
           "word-type":[
              "pronoun",
              "determiner",
              "verb"
           ]
        },
        "your":{
           "definition-es":"tu",
           "pronunciation":"//upload.wikimedia.org/wikipedia/commons/3/3b/En-uk-your.ogg",
           "word-type":[
              "determiner"
           ]
        },
        "you\u2032re":{
           "definition-es":"Tú eres",
           "pronunciation":"",
           "word-type":[
              
           ]
        }
     }
      `);
    });
  };

  useEffect(() => {
    const fetchSubtitles = async () => {
      const subtitleRaw: string = await getSubtitlesRaw();
      const subtitles: SubtitleType = JSON.parse(subtitleRaw);

      setSubtitles(subtitles);
    };
    fetchSubtitles();
  }, []);

  useEffect(() => {
    const fetchDictionary = async () => {
      const dictionaryRaw: string = await getDictionaryRaw();
      setDictionary(new Map(Object.entries(JSON.parse(dictionaryRaw))));
    };
    fetchDictionary();
  }, []);

  const onWordForTranslationSelected = () => {
      setPlayingState({
         playing: false,
         blur: 5,
         showOverlay: true
      });
  };

  const renderLine = (line: SubtitleLine): JSX.Element => {
    return (
      <>
        {line.words.map(({ word }, index) => (
          <React.Fragment key={index}>
            <OverlayTrigger
              onEntered={onWordForTranslationSelected}
              onExit={() => {
                  setPlayingState({
                     playing: true,
                     blur: 0,
                     showOverlay: false
                  });
              }}
              trigger="click"
              rootClose
              placement="top"
              overlay={
                <Popover id="popover-positioned-top">
                  <Popover.Title
                    style={{
                      background: 'grey',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                      <b style={{ color: '#FFF' }}>{word}</b>
                      <button
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                        }}
                        onClick={() => document.body.click()}>
                        {/* X button */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#FFF"
                          className="bi bi-x-circle-fill"
                          viewBox="0 0 16 16">
                          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                        </svg>
                      </button>
                    </div>
                  </Popover.Title>
                  {resolveTranslationBody(word)}
                </Popover>
              }>
              <button
                style={{
                  border: 'none',
                  background: 'none',
                  color: index === currentWordIndex ? 'yellow' : 'white',
                  padding: 0,
                }}>
                {word}
              </button>
            </OverlayTrigger>
            <pre> </pre>
          </React.Fragment>
        ))}
      </>
    );
  };

  const createSubtitleLine = (line: SubtitleLine | undefined): JSX.Element | null => {
    if (line) {
      return renderLine(line);
    } else {
      return null;
    }
  };

  const onTranslationResolved = () => {
   setPlayingState({
      playing: true,
      blur: 0,
      showOverlay: false
   })
  };

  const resolveTranslationBody = (word: string): JSX.Element => {
    const entry = dictionary ? dictionary.get(word.toLowerCase()) : undefined;

    const resolve = (entry: DictionaryEntry | undefined): JSX.Element => {
      if (entry) {
        const { pronunciation } = entry;

        return (
          <>
            <p>
              <b>Definición:</b> {entry['definition-es']}
            </p>
            <p>
              <b>Pronunciación:</b>
              {!!pronunciation && <AudioPlayer src={pronunciation} />}
            </p>
            <p>
              <b>Usos: </b>
            </p>
            <ul>
              {entry['word-type'].map((type) => {
                return <li>{type}</li>;
              })}
            </ul>
          </>
        );
      } else {
        return <span>No se encontró una definición en el diccionario</span>;
      }
    };

    return (
      <div
        style={{
          padding: '0.5rem 1rem',
          background: 'rgba(0, 0, 0, 0.59) none repeat scroll 0% 0%',
          color: '#FFF',
        }}>
        {resolve(entry)}
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
        }}>
        <ReactPlayer
          width={width}
          pip={false}
          playing={playingState.playing}
          height={height}
          url="https://www.youtube.com/watch?v=hLQl3WQQoQ0"
          onProgress={onProgressCallback}
          progressInterval={10}
          style={{
            filter: `blur(${playingState.blur}px)`,
          }}
        />
        {inSubtitleRange && (
          <div
            style={{
              position: 'absolute',
              top: '80%',
              zIndex: 2,
            }}>
            <div
              style={{
                color: 'white',
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
              }}>
              {createSubtitleLine(subtitles?.lines[currentSubtitleIndex])}
            </div>
          </div>
        )}
        {playingState.showOverlay && (
          <div
            style={{
              height,
              width,
              position: 'absolute',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={onTranslationResolved}>
            <button
              onClick={onTranslationResolved}
              style={{
                border: 'none',
                background: 'none',
              }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="50"
                height="50"
                fill="#FFF"
                className="bi bi-play-circle-fill"
                viewBox="0 0 16 16">
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default VideoPlayer;
